import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { useListGraphCategories } from '@squareup/dex-feature-hooks-graph';
import { SpecVersionPickerContainer } from '@squareup/dex-feature-shared-main-specification-version-picker';
import { ApiAside, ReferencePage, ReferenceItem } from '@squareup/dex-tech-ui';
import { ReferenceHrefBuilders } from '@squareup/dex-tech-utils-routing';
import { Head } from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Box } from '@squareup/dex-ui-shared-base';
import {
  getNextPublicApiReferenceNameOrDefault,
  getNextPublicSquareDocsURL,
} from '@squareup/dex-utils-environment';
import { filterForValidApis } from '@squareup/dex-utils-oas';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import { humanizeName, stripMarkdown } from '@squareup/dex-utils-text';
import React, { ReactNode, useRef } from 'react';

import { SquareVersionPage } from '../../../shared/SquareVersionPage';

const pageDescription =
  'This API Reference has all the detailed API endpoint information you need to build with our APIs. Square APIs let you take payments securely, integrate your application with Square first-party products, and do so much more';

const Categories = () => {
  const query = useParams();
  const { categories, error } = useListGraphCategories();

  if (error) {
    return null;
  }

  if (!categories) {
    return null;
  }

  return (
    <>
      {categories.map((category, index) => {
        return (
          <ReferenceItem
            key={category.id}
            title={category.name}
            description={category.description}
            summary={category.summary}
            inlineHeadingChild={
              index === 0 ? (
                <SpecVersionPickerContainer variant="reference" />
              ) : undefined
            }
            subItems={filterForValidApis(category.apis).map((api) => ({
              item: {
                // `api` contains getters which cannot be spread, so instead,
                // required properties are explicitly specified below:
                category: api.category,
                description: api.description,
                displayName: api.displayName,
                endpoints: api.endpoints,
                entities: api.entities,
                externalDocsList: api.externalDocsList,
                id: api.id,
                name: api.name,
                summary: api.summary,
                title: humanizeName(api.name, { style: 'sentence' }),
              },
              href: ReferenceHrefBuilders.versionApi({
                api: `${api.id}-api`,
                square_version: query?.square_version as string,
              }),
              key: api.id,
            }))}
          />
        );
      })}
      <ReferenceItem
        key="additional-resources"
        title="Additional Resources"
        summary="Other resources to help you build"
        subItems={[
          {
            key: 'object-index',
            item: {
              title: 'Object Index',
              summary: 'Browse through all datatypes of the Square API',
            },
            href: ReferenceHrefBuilders.versionObjects({
              square_version: query?.square_version as string,
            }),
          },
        ]}
      />
    </>
  );
};

const PageContent = () => {
  const aside = useRef<ApiAside>({
    content: <Categories />,
    trackingId: 'square-version-categories',
  });
  getDeveloperApi().useGetUserInfoQuery();

  return (
    <>
      <Head
        description={stripMarkdown(pageDescription)}
        preconnectUrl={getNextPublicSquareDocsURL()}
        title={getNextPublicApiReferenceNameOrDefault()}
      ></Head>
      <ReferencePage
        eyebrow={'Square API'}
        title="API Reference"
        disableVersionSwitcherDesktop={true}
        description={
          <Box>
            <Markdown
              markdown={`[Square API guides](/docs) have all the information you need to build for sellers of every size.
                  Our guides also provide the context to understand how our SDKs and APIs work together with Square hardware, software, and the Square Online Store.\n\n
                  New to Square development? Learn more about [working with the Square API](https://developer.squareup.com).\n\n
                  ${pageDescription}\n\n
                  Whether you’re customizing Square for your own business, publishing in the Square App Marketplace, or working one-on-one with Square sellers, the [Square Developer platform](/) has the resources you need.`}
            ></Markdown>
          </Box>
        }
        aside={aside.current}
      />
    </>
  );
};

class Page extends SquareVersionPage {
  public static override routeHandlerName = '[square_version]';

  protected override getPageContent(): ReactNode {
    return super.getPageContent({
      children: <PageContent />,
    });
  }
}

export default Page;
